function initEliteCards(sectionRoot) {
  const cards = [...sectionRoot.querySelectorAll('.elite-cards__item')];
  const prevBtn = sectionRoot.querySelector('[data-elite-prev]');
  const nextBtn = sectionRoot.querySelector('[data-elite-next]');
  const slider = sectionRoot.querySelector('.elite-cards__main');

  if (!cards.length || !prevBtn || !nextBtn || !slider) return;

  const total = cards.length;
  const step = (Math.PI * 2) / total;
  const BASE_ROTATION = -Math.PI / 2;
  const EDGE_EPSILON = 0.035;

  let activeIndex = 0;
  let contentTimer;
  const ORBIT_DURATION = 1000;

  function getMetrics() {
    const viewportWidth = slider.clientWidth || window.innerWidth;
    const cardWidth = cards[0]?.offsetWidth || 263;

    const isMobile = window.innerWidth < 769;
    const isSmallTablet = window.innerWidth >= 769 && window.innerWidth < 990;
    const isLaptop = window.innerWidth >= 990 && window.innerWidth < 1281;

    const desiredRadiusX = isSmallTablet ? 360 : isLaptop ? 500 : 490;
    const safeGap = isSmallTablet ? 10 : isLaptop ? 18 : 40;
    const maxRadiusX = Math.max(120, (viewportWidth - cardWidth) / 2 - safeGap);

    return {
      radiusX: isMobile ? Math.round(viewportWidth * 0.52) : Math.min(desiredRadiusX, maxRadiusX),
      radiusY: isMobile ? 28 : isSmallTablet ? 150 : isLaptop ? 182 : 225,
      centerY: isMobile ? 85 : isSmallTablet ? 81 : isLaptop ? 84 : 83,
      isMobile,
      isSmallTablet,
      isLaptop,
    };
  }

  function normalizeAngle(angle) {
    const twoPi = Math.PI * 2;
    return ((angle % twoPi) + twoPi) % twoPi;
  }

  function renderOrbit() {
    const { radiusX, radiusY, centerY, isMobile, isSmallTablet, isLaptop } = getMetrics();

    const rotation = BASE_ROTATION - (activeIndex * step);

    cards.forEach((card, index) => {
      const angle = index * step + rotation;
      const normalized = normalizeAngle(angle);
      const isFrontHalf =
        normalized > (Math.PI + EDGE_EPSILON) &&
        normalized < ((Math.PI * 2) - EDGE_EPSILON);

      const isActive = index === activeIndex;

      const frontness = isFrontHalf ? -Math.sin(angle) : -0.25;
      const emphasis = Math.max(0, frontness);

      const sideLiftAmount = isMobile ? 90 : isSmallTablet ? 76 : isLaptop ? 100 : 120;
      const activeLiftAmount = isMobile ? 220 : isSmallTablet ? 60 : isLaptop ? 76 : 90;

      const sideLift = isFrontHalf && !isActive
        ? Math.abs(Math.cos(angle)) * sideLiftAmount
        : 0;

      const activeLift = isFrontHalf && isActive
        ? activeLiftAmount
        : 0;

      const baseX = Math.cos(angle) * radiusX;

      const mobileSidePush =
        isMobile && isFrontHalf && !isActive
          ? (baseX > 0 ? 180 : -180)
          : 0;

      const tabletSidePush =
        isSmallTablet && isFrontHalf && !isActive
          ? (baseX > 0 ? 45 : -45)
          : 0;

      const laptopSidePush =
        isLaptop && isFrontHalf && !isActive
          ? (baseX > 0 ? 55 : -55)
          : 0;

      const desktopSidePush =
        !isMobile && !isSmallTablet && !isLaptop && isFrontHalf && !isActive
          ? (baseX > 0 ? 90 : -90)
          : 0;

      const x = baseX + mobileSidePush + tabletSidePush + laptopSidePush + desktopSidePush;

      const desktopSideDrop =
        !isMobile && !isSmallTablet && !isLaptop && isFrontHalf && !isActive
          ? 56
          : 0;

      const y = Math.sin(angle) * radiusY - sideLift - activeLift + desktopSideDrop;

      const scale = isMobile
        ? (isFrontHalf ? (isActive ? 1 : 0.68 + emphasis * 0.18) : 0.56)
        : isFrontHalf
          ? isSmallTablet
            ? 0.66 + emphasis * 0.2
            : isLaptop
              ? 0.68 + emphasis * 0.22
              : 0.72 + emphasis * 0.3
          : 0.56;

      const opacity = isFrontHalf ? (isMobile && !isActive ? 0.72 : 1) : 0;
      const blur = isMobile ? 0 : isFrontHalf ? (1 - emphasis) * 0.8 : 0;
      const zIndex = isFrontHalf ? (isActive ? 200 : Math.round(100 + emphasis * 120)) : 0;
      const tilt = (x / (radiusX || 1)) * (isMobile ? 28 : isSmallTablet ? 52 : isLaptop ? 52 : 40);

      card.style.top = `${centerY}%`;
      card.style.setProperty('--x', `${x}px`);
      card.style.setProperty('--y', `${y}px`);
      card.style.setProperty('--scale', scale.toFixed(3));
      card.style.setProperty('--rot', `${tilt.toFixed(2)}deg`);
      card.style.setProperty('--opacity', opacity.toFixed(3));
      card.style.setProperty('--z', zIndex);
      card.style.setProperty('--blur', `${blur.toFixed(2)}px`);

      if (isFrontHalf) {
        card.style.visibility = 'visible';
        card.style.pointerEvents = isActive ? 'auto' : 'none';
      } else {
        card.style.visibility = 'hidden';
        card.style.pointerEvents = 'none';
      }
    });
  }

  const controls = sectionRoot.querySelector('.elite-cards__controls');

  function positionControls() {
    if (!controls || window.innerWidth >= 769) return;
    const h = slider.offsetHeight;
    const { centerY } = getMetrics();
    const cardCenter = (centerY / 100) * h - 248; // 248 = activeLift(220) + radiusY(28)
    let maxMargin = 0;
    cards.forEach((card) => {
      const content = card.querySelector('.elite-cards__item-content');
      if (!content) return;
      const contentBottom = cardCenter + 184 + 24 + content.offsetHeight;
      const overhead = contentBottom - h;
      maxMargin = Math.max(maxMargin, Math.ceil(overhead) + 28);
    });
    controls.style.marginTop = `${Math.max(0, maxMargin)}px`;
  }

  function syncActiveContent(initial = false) {
    clearTimeout(contentTimer);

    const applyActive = () => {
      cards.forEach((card, index) => {
        card.classList.toggle('active', index === activeIndex);
      });
    };

    if (initial) {
      applyActive();
    } else {
      contentTimer = setTimeout(applyActive, ORBIT_DURATION * 0.72);
    }
  }

  function nextCard() {
    cards.forEach(card => card.classList.remove('active'));
    activeIndex = (activeIndex + 1) % total;
    renderOrbit();
    syncActiveContent();
  }

  function prevCard() {
    cards.forEach(card => card.classList.remove('active'));
    activeIndex = (activeIndex - 1 + total) % total;
    renderOrbit();
    syncActiveContent();
  }

  prevBtn.addEventListener('click', prevCard);
  nextBtn.addEventListener('click', nextCard);

  let resizeTimer;
  window.addEventListener('resize', () => {
    clearTimeout(resizeTimer);
    resizeTimer = setTimeout(() => {
      renderOrbit();
      positionControls();
      syncActiveContent(true);
    }, 120);
  });

  window.addEventListener('load', positionControls);

  let touchStartX = 0;
  let touchStartY = 0;
  let touchEndX = 0;
  let touchEndY = 0;
  let isSwiping = false;

  function isMobileView() {
    return window.innerWidth < 769;
  }

  slider.addEventListener(
    'touchstart',
    (e) => {
      if (!isMobileView()) return;
      const touch = e.changedTouches[0];
      touchStartX = touch.clientX;
      touchStartY = touch.clientY;
      touchEndX = touch.clientX;
      touchEndY = touch.clientY;
      isSwiping = true;
    },
    { passive: true }
  );

  slider.addEventListener(
    'touchmove',
    (e) => {
      if (!isMobileView() || !isSwiping) return;
      const touch = e.changedTouches[0];
      touchEndX = touch.clientX;
      touchEndY = touch.clientY;
    },
    { passive: true }
  );

  slider.addEventListener('touchend', () => {
    if (!isMobileView() || !isSwiping) return;

    const deltaX = touchEndX - touchStartX;
    const deltaY = touchEndY - touchStartY;
    const minSwipeDistance = 40;
    const isHorizontalSwipe = Math.abs(deltaX) > Math.abs(deltaY);

    if (isHorizontalSwipe && Math.abs(deltaX) > minSwipeDistance) {
      if (deltaX < 0) {
        nextCard();
      } else {
        prevCard();
      }
    }

    isSwiping = false;
  });

  renderOrbit();
  syncActiveContent(true);
  positionControls();
}

document.querySelectorAll('.elite-cards').forEach(initEliteCards);
